// FairFix "Deck & timber work" section — data for the images shown in the
// gallery. This file is a MIX of two kinds of images:
//
//   • Real jobs Corey has completed (his own work archive)
//   • Example images of the kind of work he can build for you
//
// STRICT HONESTY RULE (2026-08-01 correction)
// This file previously asserted every entry was the owner's own completed
// work. That claim was written on an assumption, not verified with Corey,
// and some entries are catalogue/example images, not FairFix jobs. Until
// each item is individually confirmed with the owner, the section header on
// the site reads "A mix of jobs I've done and examples of the kind of work
// I can build for you" so the page makes no claim it can't back.
//
// Per-item verification (to be filled by owner):
//   • ownWork: true   — Corey has confirmed this is his own completed job
//   • ownWork: false  — this is an example image, not a FairFix job
//   • ownWork: absent — awaiting owner confirmation; renders in the mixed
//                        section under the neutral heading
// Do NOT default the flag either way. Do NOT infer from the caption. If
// it isn't set, it isn't confirmed.
//
// Category assignments (owner-confirmed):
//   • Decks & Stairs      — decks + step/stair builds
//   • Framing & Structural — timber framing + full frame-ups
//   • Cladding & Exteriors — batten/board exterior cladding
//   • Custom Builds       — one-off finish/joinery work
//
// Location honesty rule: no job gets tagged Hobart unless the owner
// explicitly confirmed it (currently only the leafy-garden deck).
// Primrose Sands is TAS, Darwin is NT, Caulfield is VIC.

// ---- New professional shots (2026-07 owner batch) ----
import darwin640 from './processed/deck-darwin-grid-640.webp';
import darwin960 from './processed/deck-darwin-grid-960.webp';
import darwinJpg from './processed/deck-darwin-grid-960.jpg';
import darwinFullWebp from './processed/deck-darwin-full.webp';
import darwinFullJpg from './processed/deck-darwin-full.jpg';

import d2_640 from './processed/deck-2-grid-640.webp';
import d2_960 from './processed/deck-2-grid-960.webp';
import d2Jpg from './processed/deck-2-grid-960.jpg';
import d2FullWebp from './processed/deck-2-full.webp';
import d2FullJpg from './processed/deck-2-full.jpg';

import d3_640 from './processed/deck-3-grid-640.webp';
import d3_960 from './processed/deck-3-grid-960.webp';
import d3Jpg from './processed/deck-3-grid-960.jpg';
import d3FullWebp from './processed/deck-3-full.webp';
import d3FullJpg from './processed/deck-3-full.jpg';

import d4_640 from './processed/deck-4-grid-640.webp';
import d4_960 from './processed/deck-4-grid-960.webp';
import d4Jpg from './processed/deck-4-grid-960.jpg';
import d4FullWebp from './processed/deck-4-full.webp';
import d4FullJpg from './processed/deck-4-full.jpg';

import d5_640 from './processed/deck-5-grid-640.webp';
import d5_960 from './processed/deck-5-grid-960.webp';
import d5Jpg from './processed/deck-5-grid-960.jpg';
import d5FullWebp from './processed/deck-5-full.webp';
import d5FullJpg from './processed/deck-5-full.jpg';

import dp_640 from './processed/deck-primrose-grid-640.webp';
import dp_960 from './processed/deck-primrose-grid-960.webp';
import dpJpg from './processed/deck-primrose-grid-960.jpg';
import dpFullWebp from './processed/deck-primrose-full.webp';
import dpFullJpg from './processed/deck-primrose-full.jpg';

import c1_640 from './processed/cladding-1-grid-640.webp';
import c1_960 from './processed/cladding-1-grid-960.webp';
import c1Jpg from './processed/cladding-1-grid-960.jpg';
import c1FullWebp from './processed/cladding-1-full.webp';
import c1FullJpg from './processed/cladding-1-full.jpg';

import frame640 from './processed/frame-caulfield-grid-640.webp';
import frame960 from './processed/frame-caulfield-grid-960.webp';
import frameJpg from './processed/frame-caulfield-grid-960.jpg';
import frameFullWebp from './processed/frame-caulfield-full.webp';
import frameFullJpg from './processed/frame-caulfield-full.jpg';

// ---- Kept archive shots ----
import cts640 from './processed/cladding-twostorey-grid-640.webp';
import cts960 from './processed/cladding-twostorey-grid-960.webp';
import ctsJpg from './processed/cladding-twostorey-grid-960.jpg';
import ctsFullWebp from './processed/cladding-twostorey-full.webp';
import ctsFullJpg from './processed/cladding-twostorey-full.jpg';

import cwb640 from './processed/cladding-weatherboard-grid-640.webp';
import cwb960 from './processed/cladding-weatherboard-grid-960.webp';
import cwbJpg from './processed/cladding-weatherboard-grid-960.jpg';
import cwbFullWebp from './processed/cladding-weatherboard-full.webp';
import cwbFullJpg from './processed/cladding-weatherboard-full.jpg';

import pa640 from './processed/planter-after-grid-640.webp';
import pa960 from './processed/planter-after-grid-960.webp';
import paJpg from './processed/planter-after-grid-960.jpg';
import paFullWebp from './processed/planter-after-full.webp';
import paFullJpg from './processed/planter-after-full.jpg';
import pbFullWebp from './processed/planter-before-full.webp';
import pbFullJpg from './processed/planter-before-full.jpg';

import nan640 from './processed/nans-deck-grid-640.webp';
import nan960 from './processed/nans-deck-grid-960.webp';
import nanJpg from './processed/nans-deck-grid-960.jpg';
import nanFullWebp from './processed/nans-deck-full.webp';
import nanFullJpg from './processed/nans-deck-full.jpg';

// Ordered strongest-first per owner instruction. deck-primrose leads (owner
// confirmed the strongest image); cladding-1 and frame-caulfield surface next
// to lead with credibility for the full-build positioning.
export const projects = [
  {
    id: 'tommy-06',
    category: 'Formwork & Concrete',
    caption: "Stripped. Two-chamber pit, off-form finish. — Tommy's work.",
    alt: 'Stripped two-chamber stormwater pit with a clean off-form concrete finish.',
    thumb: { webp640: '/formwork/tommy-06-400.webp', webp960: '/formwork/tommy-06-800.webp', jpg: '/formwork/tommy-06-800.jpg' },
    full: { webp: '/formwork/tommy-06-1600.webp', jpg: '/formwork/tommy-06-1600.jpg' },
  },
  {
    id: 'tommy-08',
    category: 'Formwork & Concrete',
    caption: "Exposed aggregate driveway — test panels first, then the pour. — Tommy's work.",
    alt: 'Exposed aggregate test panels laid out on the ground before the driveway pour.',
    thumb: { webp640: '/formwork/tommy-08-400.webp', webp960: '/formwork/tommy-08-800.webp', jpg: '/formwork/tommy-08-800.jpg' },
    full: { webp: '/formwork/tommy-08-1600.webp', jpg: '/formwork/tommy-08-1600.jpg' },
  },
  {
    id: 'deck-primrose',
    category: 'Decks & Stairs',
    caption: 'Deck build — Primrose Sands, TAS',
    alt: 'Elevated hardwood deck with wire balustrade, dark timber handrail, brick piers and timber stairs down to a paved area.',
    thumb: { webp640: dp_640, webp960: dp_960, jpg: dpJpg },
    full: { webp: dpFullWebp, jpg: dpFullJpg },
  },
  {
    id: 'cladding-timber-ceiling',
    category: 'Custom Builds',
    caption: 'Timber-lined ceiling — custom batten detail',
    alt: 'Fan-perspective view up at a timber batten and slat ceiling under an outdoor covered area, with brick walls and a bright blue sky visible beyond.',
    thumb: { webp640: c1_640, webp960: c1_960, jpg: c1Jpg },
    full: { webp: c1FullWebp, jpg: c1FullJpg },
  },
  {
    id: 'frame-caulfield',
    category: 'Framing & Structural',
    caption: 'House frame — Caulfield, VIC. Built with just me and my partner.',
    alt: 'A full timber house frame with pitched roof trusses erected on a concrete slab under a bright blue sky.',
    thumb: { webp640: frame640, webp960: frame960, jpg: frameJpg },
    full: { webp: frameFullWebp, jpg: frameFullJpg },
  },
  {
    id: 'deck-darwin',
    category: 'Decks & Stairs',
    caption: 'Deck build in Darwin, NT — part of the crew',
    alt: 'A wide oiled hardwood deck wrapping a timber-clad home, with cascading corner stairs, a wire balustrade and tropical planting.',
    thumb: { webp640: darwin640, webp960: darwin960, jpg: darwinJpg },
    full: { webp: darwinFullWebp, jpg: darwinFullJpg },
  },
  {
    id: 'deck-5',
    category: 'Decks & Stairs',
    caption: 'Deck and pergola build',
    alt: 'Elevated hardwood deck with a polycarbonate pergola roof, wire balustrade and timber handrail against a brick home.',
    thumb: { webp640: d5_640, webp960: d5_960, jpg: d5Jpg },
    full: { webp: d5FullWebp, jpg: d5FullJpg },
  },
  {
    id: 'deck-2',
    category: 'Decks & Stairs',
    caption: 'Hardwood deck and entry steps',
    alt: 'A wet, freshly-oiled red-stained hardwood deck with wide entry steps and a timber-and-wire handrail against a blue weatherboard home.',
    thumb: { webp640: d2_640, webp960: d2_960, jpg: d2Jpg },
    full: { webp: d2FullWebp, jpg: d2FullJpg },
  },
  {
    id: 'deck-4',
    category: 'Decks & Stairs',
    caption: 'Low-profile hardwood deck',
    alt: 'A low-profile hardwood deck with a wide step-down edge in front of a brick home, warm sunlight raking across the boards.',
    thumb: { webp640: d4_640, webp960: d4_960, jpg: d4Jpg },
    full: { webp: d4FullWebp, jpg: d4FullJpg },
  },
  {
    id: 'nans-deck',
    category: 'Decks & Stairs',
    caption: 'Turned an unused corner of the yard into somewhere quiet to sit — timber has a way of doing that.',
    alt: 'Finished hardwood deck with timber privacy screen in a leafy Hobart garden, built by FairFix',
    thumb: { webp640: nan640, webp960: nan960, jpg: nanJpg },
    full: { webp: nanFullWebp, jpg: nanFullJpg },
  },
  {
    id: 'deck-3',
    category: 'Decks & Stairs',
    caption: 'Freshly-finished hardwood deck',
    alt: 'A rich red-brown freshly finished hardwood deck with dark brick piers rising from it, viewed from above.',
    thumb: { webp640: d3_640, webp960: d3_960, jpg: d3Jpg },
    full: { webp: d3FullWebp, jpg: d3FullJpg },
  },
  {
    id: 'cladding-twostorey',
    category: 'Cladding & Exteriors',
    caption: 'Full exterior cladding, two-storey build',
    alt: 'Modern two-storey home clad in vertical timber battens, with a scaffold tower alongside during the build.',
    thumb: { webp640: cts640, webp960: cts960, jpg: ctsJpg },
    full: { webp: ctsFullWebp, jpg: ctsFullJpg },
  },
  {
    id: 'cladding-weatherboard',
    category: 'Cladding & Exteriors',
    caption: 'Weatherboard cladding, full exterior',
    alt: 'Horizontal timber weatherboard cladding on a home, photographed in low sun from an upward angle.',
    thumb: { webp640: cwb640, webp960: cwb960, jpg: cwbJpg },
    full: { webp: cwbFullWebp, jpg: cwbFullJpg },
  },
  {
    id: 'planter',
    category: 'Custom Builds',
    caption: 'Custom planter box — before & after',
    alt: 'Finished stained timber planter box filled with plants and pebbles, lit at night.',
    thumb: { webp640: pa640, webp960: pa960, jpg: paJpg },
    full: { webp: paFullWebp, jpg: paFullJpg },
    beforeAfter: {
      before: { webp: pbFullWebp, jpg: pbFullJpg, alt: 'Timber planter box frame under construction in the workshop under a work light.' },
      after: { webp: paFullWebp, jpg: paFullJpg, alt: 'Finished stained timber planter box filled with plants and pebbles, lit at night.' },
    },
  },
];
